import React from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { compose, withProps } from 'recompose';
import { xor } from 'ramda';

import { t } from '../../../../modules/infra/i18n';

import ReportBox from './ReportBox';
import Num from '../../../common/Num';
import { grossSalesKinds } from '../constants';
import { MoneyFitMedium } from './MoneyFit';
import withCanSeeGrossSalesMembership from '../hoc/withCanSeeGrossSalesMembership';
import GrossSalesBreakdown from './GrossSalesBreakdown';

const GrossSales = (props) => {
  const renderGrossSalesBreakdown = (isLargeSize) => (
    <GrossSalesBreakdown
      isLargeSize={isLargeSize}
      grossSales={props.grossSales}
      grossTotal={props.grossTotal}
      newMembershipGrossTotal={props.newMembershipGrossTotal}
      recurringMembershipGrossTotal={props.recurringMembershipGrossTotal}
      productGrossTotal={props.productGrossTotal}
      serviceGrossTotal={props.serviceGrossTotal}
      isMembershipDataVisible={props.isMembershipDataVisible}
    />
  );

  return (
    <ReportBox title={t('reports:dashboard.total_gross_sales')}>
      <div className="u-text-center u-ph+">
        <div display-if={!props.isTaxVisible}>
          <div
            className="c-report-stat c-report-stat--large u-mt+ u-mb--"
            data-automation-id="text:totalGrossSalesAmount"
          >
            {renderGrossSalesBreakdown(true)}
          </div>
          <div className="c-report-label u-mb+">{t('reports:dashboard.total_gross_sales')}</div>
        </div>
        <div display-if={props.isTaxVisible}>
          <div
            className={cx('c-report-stat u-mb--', {
              'c-report-stat--large': !props.dataPointsExtraSmall,
              'u-mt--': props.dataPointsExtraSmall,
              'u-mt+': !props.dataPointsExtraSmall,
            })}
          >
            {renderGrossSalesBreakdown(!props.dataPointsExtraSmall)}
          </div>
          <div className="c-report-label">{t('reports:dashboard.gross_sales')}</div>
          <div className="c-report-stat c-report-stat--large u-mt u-mb--">
            <MoneyFitMedium automationId="text:grossSalesAmount">{props.grossSales + props.taxes}</MoneyFitMedium>
          </div>
          <div
            className={cx('c-report-label', {
              'u-mb-': props.dataPointsExtraSmall,
              'u-mb+': !props.dataPointsExtraSmall,
            })}
          >
            {t('reports:dashboard.gross_sales_plus_taxes')}
          </div>
        </div>
        <div className="c-horizontal-divider u-mt" />
        <div
          className={cx('layout', {
            'u-mt': props.dataPointsSmall || props.dataPointsExtraSmall,
            'u-mt+': !(props.dataPointsSmall || props.dataPointsExtraSmall),
          })}
        >
          <div className="layout__item u-1/2">
            <div
              className={cx('c-report-stat u-mb-', {
                'c-report-stat--small': props.dataPointsSmall,
                'c-report-stat--tiny': props.dataPointsExtraSmall,
              })}
            >
              <Num automationId="text:serviceSales">{props.serviceSales}</Num>
            </div>
            <div className="c-report-label">{t('reports:dashboard.service_sales')}</div>
          </div>
          <div className="layout__item u-1/2">
            <div
              className={cx('c-report-stat u-mb-', {
                'c-report-stat--small': props.dataPointsSmall,
                'c-report-stat--tiny': props.dataPointsExtraSmall,
              })}
            >
              <Num automationId="text:productSales">{props.productSales}</Num>
            </div>
            <div className="c-report-label">{t('reports:dashboard.product_sales')}</div>
          </div>
          <div className="layout__item u-1/2" display-if={props.isMembershipDataVisible}>
            <div className="c-report-stat c-report-stat--tiny u-mb-">
              <Num automationId="text:newMembershipCount">{props.newMembershipCount}</Num>
            </div>
            <div className="c-report-label">{t('reports:dashboard.new_memberships')}</div>
          </div>
          <div className="layout__item u-1/2" display-if={props.isMembershipDataVisible}>
            <div className="c-report-stat c-report-stat--tiny u-mb-">
              <Num automationId="text:recurringMembershipCount">{props.recurringMembershipCount}</Num>
            </div>
            <div className="c-report-label">{t('reports:dashboard.recurring_memberships')}</div>
          </div>
        </div>
      </div>
    </ReportBox>
  );
};

GrossSales.propTypes = {
  grossSales: PropTypes.number.isRequired,
  taxes: PropTypes.number.isRequired,
  serviceSales: PropTypes.number.isRequired,
  productSales: PropTypes.number.isRequired,
  newMembershipCount: PropTypes.number.isRequired,
  recurringMembershipCount: PropTypes.number.isRequired,
  grossTotal: PropTypes.number.isRequired, // eslint-disable-line react/no-unused-prop-types
  newMembershipGrossTotal: PropTypes.number.isRequired, // eslint-disable-line react/no-unused-prop-types
  recurringMembershipGrossTotal: PropTypes.number.isRequired, // eslint-disable-line react/no-unused-prop-types
  productGrossTotal: PropTypes.number.isRequired, // eslint-disable-line react/no-unused-prop-types
  serviceGrossTotal: PropTypes.number.isRequired, // eslint-disable-line react/no-unused-prop-types
  isTaxVisible: PropTypes.bool.isRequired,
  isMembershipDataVisible: PropTypes.bool.isRequired,
  dataPointsSmall: PropTypes.bool.isRequired,
  dataPointsExtraSmall: PropTypes.bool.isRequired,
};

export default compose(
  withCanSeeGrossSalesMembership,
  withProps((props) => {
    const kind = props.grossSalesKind || grossSalesKinds.default;
    const isTaxVisible = kind === grossSalesKinds.grossSalesPlusTaxes;
    return {
      isTaxVisible,
      isMembershipDataVisible: props.canSeeMembershipsData,
      dataPointsSmall: xor(isTaxVisible, props.canSeeMembershipsData),
      dataPointsExtraSmall: isTaxVisible && props.canSeeMembershipsData,
    };
  })
)(GrossSales);
